import { useState, useCallback } from "react";
import { Platform, Alert } from "react-native";
import { useStytch } from "./useStytch";
import { getAuthCallbackUrl, saveAuthReturnUrl } from "./getAuthCallbackUrl";

type OAuthProvider = "google" | "apple";

interface UseOAuthReturn {
  startOAuth: (provider: OAuthProvider) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function useOAuth(): UseOAuthReturn {
  const stytch = useStytch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startOAuth = useCallback(
    async (provider: OAuthProvider) => {
      setIsLoading(true);
      setError(null);

      try {
        const callbackUrl = getAuthCallbackUrl();
        saveAuthReturnUrl();

        if (provider === "google") {
          await stytch.oauth.google.start({
            login_redirect_url: callbackUrl,
            signup_redirect_url: callbackUrl,
            provider_params: { prompt: "select_account" },
          });
        } else if (provider === "apple") {
          await stytch.oauth.apple.start({
            login_redirect_url: callbackUrl,
            signup_redirect_url: callbackUrl,
          });
        }
      } catch (err) {
        console.error(`OAuth ${provider} error:`, err);
        const errorMessage =
          err instanceof Error ? err.message : "Authentication failed";
        setError(errorMessage);

        if (Platform.OS !== "web") {
          Alert.alert(
            "Authentication Error",
            `Failed to sign in with ${provider}. Please try again.`,
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [stytch],
  );

  return {
    startOAuth,
    isLoading,
    error,
  };
}
